import { ApplicationConfig, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withHashLocation,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
  RouteReuseStrategy
} from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { DropdownModule, SidebarModule } from '@coreui/angular';
import { IconSetService } from '@coreui/icons-angular';
import { routes } from './app.routes';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { ParamAwareRouteReuseStrategy } from './shared/param-route-reuse-strategy';
import { LocaleService } from './service/locale.service';
import { authTokenInterceptor } from './service/auth-token.interceptor';

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  // Absolute path — relative `./assets/...` can resolve against the current route
  // (PathLocationStrategy) and hit SPA try_files → HTML instead of JSON in prod.
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

export function localeBootstrapFactory(locale: LocaleService): () => Promise<void> {
  return () => locale.bootstrap();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload'
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled'
      }),
      withEnabledBlockingInitialNavigation(),
      withViewTransitions({
        onViewTransitionCreated: ({ transition }) => {
          // 背景分頁／隱藏文件時瀏覽器會 skip transition，未處理會變成 Unhandled Promise Rejection
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            transition.skipTransition();
          }
          void transition.finished.catch(() => undefined);
        },
      }),
      withHashLocation()
    ),
    importProvidersFrom(
      SidebarModule,
      DropdownModule,
      TranslateModule.forRoot({
        defaultLanguage: 'zh-TW',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),
    IconSetService,
    provideAnimations(),
    provideHttpClient(withInterceptors([authTokenInterceptor])),
    {
      provide: APP_INITIALIZER,
      useFactory: localeBootstrapFactory,
      deps: [LocaleService],
      multi: true,
    },
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy
    },
    {
      provide: RouteReuseStrategy,
      useClass: ParamAwareRouteReuseStrategy
    }
  ]
};
